import {
  Grid,
  Typography,
  Box,
  FormControl,
  Button,
  Dialog,
  DialogContent,
  DialogActions,
  IconButton,
  Select,
  MenuItem,
  OutlinedInput,
  DialogTitle,
  Paper,
  Card,
  CardActionArea,
  CardContent,
  Chip,
  Divider,
  InputAdornment,
  Stack,
} from "@mui/material";
import { useTheme, alpha } from "@mui/material/styles";
import { grey } from "@mui/material/colors";
import TextField from "@mui/material/TextField";
import React, {
  useEffect,
  useState,
  useMemo,
  useCallback,
  useRef,
} from "react";
import Keypad from "./../../../components/Keypad";
import CloseIcon from "@mui/icons-material/Close";
import EditIcon from "@mui/icons-material/Edit";
import Webcam from "react-webcam";
import { SALENG_SHOP_API_PROD } from "../../../config";
import { formatNumber } from "../../../utils/formatNumber";
import { useProductsBranch } from "../../../hooks/useProductsBranch";

const SALENG_SHOP_API = SALENG_SHOP_API_PROD;
const beep = new Audio("/sounds/beep.mp3");

const ProductSelectDialog = ({
  open,
  onClose,
  onConfirm,
  products,
  categories,
  initialSelectedId,
}) => {
  const theme = useTheme();
  const [catFilter, setCatFilter] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedId, setSelectedId] = useState(initialSelectedId || null);

  useEffect(() => {
    if (open) {
      setCatFilter(null);
      setSearchQuery("");
      setSelectedId(initialSelectedId || null);
    }
  }, [open, initialSelectedId]);

  const colorPalette = [
    theme.palette.primary.main,
    theme.palette.secondary.main,
    theme.palette.error.main,
    theme.palette.warning.main,
    theme.palette.info.main,
    theme.palette.success.main,
  ];

  const catColorMap = useMemo(() => {
    return categories.reduce((m, c, i) => {
      m[c.name] = colorPalette[i % colorPalette.length];
      return m;
    }, {});
  }, [categories]);

  const filtered = useMemo(() => {
    return products.filter((p) => {
      if (catFilter && p.category.name !== catFilter) return false;
      if (
        searchQuery &&
        !p.title.toLowerCase().includes(searchQuery.toLowerCase())
      )
        return false;
      return true;
    });
  }, [products, catFilter, searchQuery]);

  const toggleSelect = (id) =>
    setSelectedId((prev) => (prev === id ? null : id));

  const confirm = () => {
    const prod = products.find((p) => p.id === selectedId);
    if (prod) onConfirm(prod);
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="xl">
      <DialogTitle>เลือกสินค้า</DialogTitle>
      <DialogContent sx={{ display: "flex", p: 2 }}>
        {/* Sidebar หมวด */}
        <Paper
          variant="outlined"
          sx={{ width: 240, maxHeight: "70vh", overflowY: "auto", mr: 2 }}
        >
          <Box
            onClick={() => setCatFilter(null)}
            sx={{
              p: 2,
              cursor: "pointer",
              bgcolor: catFilter === null ? "action.selected" : "transparent",
            }}
          >
            ทั้งหมด
          </Box>
          {[...new Map(categories.map((cat) => [cat.name, cat])).values()].map(
            (cat) => (
              <Box
                key={cat.id}
                onClick={() => setCatFilter(cat.name)}
                sx={{
                  p: 2,
                  cursor: "pointer",
                  bgcolor:
                    catFilter === cat.name ? "action.selected" : "transparent",
                }}
              >
                {cat.name}
              </Box>
            )
          )}
        </Paper>

        {/* Main เลือกสินค้า */}
        <Box flex={1} display="flex" flexDirection="column">
          <TextField
            fullWidth
            placeholder="ค้นหาชื่อสินค้า..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            sx={{ mb: 2 }}
          />
          <Box flex={1} overflow="auto">
            <Grid container spacing={2}>
              {filtered.map((p) => {
                const isSel = selectedId === p.id;
                return (
                  <Grid item xs={6} sm={4} md={3} key={p.id}>
                    <Card
                      variant="outlined"
                      sx={{
                        height: "100%",
                        borderColor: isSel ? "success.main" : undefined,
                        bgcolor: isSel
                          ? theme.palette.grey[300]
                          : alpha(
                              catColorMap[p.category.name] || grey[200],
                              0.2
                            ),
                      }}
                    >
                      <CardActionArea onClick={() => toggleSelect(p.id)}>
                        <CardContent>
                          <Typography noWrap>{p.title}</Typography>
                          <Typography variant="caption" color="text.secondary">
                            {p.subCategory?.name}
                          </Typography>
                        </CardContent>
                      </CardActionArea>
                    </Card>
                  </Grid>
                );
              })}
            </Grid>
          </Box>
        </Box>
      </DialogContent>
      <DialogActions>
        <Chip
          label={`เลือกแล้ว ${selectedId ? 1 : 0} ชิ้น`}
          color={selectedId ? "success" : "default"}
        />
        <Button size="large" onClick={() => setSelectedId(null)}>
          ล้าง
        </Button>
        <Button
          variant="contained"
          size="large"
          onClick={confirm}
          disabled={!selectedId}
        >
          ยืนยัน
        </Button>
      </DialogActions>
    </Dialog>
  );
};

const SimpleDialog = (props) => {
  const theme = useTheme();
  const { open, onClose, onSubmit, selectedValue } = props;

  // ---------------------- state ----------------------
  const [price, setPrice] = useState(0);
  const [weight, setWeight] = useState(0);
  const [deduct, setDeduct] = useState(0);

  const [mute, setMute] = useState(false);
  const [focused, setFocused] = useState("weight"); // "weight" | "deduct" | null

  const lastWeightRef = useRef(null);
  const stableStartTimeRef = useRef(null);
  const beepPlayedRef = useRef(false);

  const webcamRef = useRef(null);
  const [inputValue, setInputValue] = useState(0);
  const { products } = useProductsBranch();

  // ==== NEW: main product editing ====
  const [openMainProdDialog, setOpenMainProdDialog] = useState(false);
  const [mainProduct, setMainProduct] = useState(null); // {id,title,categoryId,...}

  // ---------------------- helpers ----------------------
  const categories = useMemo(() => {
    const seen = new Set();
    return products
      .map((p) => p.category)
      .filter((cat) => {
        if (!cat) return false;
        if (seen.has(cat.name)) return false;
        seen.add(cat.name);
        return true;
      });
  }, [products]);

  const colorPalette = [
    theme.palette.primary.main,
    theme.palette.secondary.main,
    theme.palette.error.main,
    theme.palette.warning.main,
    theme.palette.info.main,
    theme.palette.success.main,
  ];

  const catColorMap = useMemo(() => {
    return categories.reduce((m, c, i) => {
      m[c.name] = colorPalette[i % colorPalette.length];
      return m;
    }, {});
  }, [categories, colorPalette]);

  // sync mainProduct เมื่อเปิด dialog หรือ selectedValue เปลี่ยน
  useEffect(() => {
    navigator.mediaDevices.enumerateDevices().then(devices => {
      console.log("🔍 Devices:", devices);
    });
    if (selectedValue?.id && selectedValue?.title) {
      setMainProduct({
        id: selectedValue.id,
        title: selectedValue.title,
        categoryId: selectedValue.categoryId,
      });
    } else {
      setMainProduct(null);
    }
  }, [selectedValue]);

  // ------------- effects & data fetching -------------
  useEffect(() => {
    if (selectedValue) {
      const { finalPrice: p = 0, weight: w = 0, deduct: d = 0 } = selectedValue;

      setPrice(p);
      setWeight(w);
      setDeduct(d);
    }

    const socket = new WebSocket("ws://localhost:3001/ws");
    socket.onmessage = handleData;

    const interval = setInterval(() => {
      if (lastWeightRef.current !== null) {
        const elapsed = Date.now() - stableStartTimeRef.current;
        if (
          elapsed >= 2500 &&
          !beepPlayedRef.current &&
          lastWeightRef.current !== 0 &&
          !mute
        ) {
          // beep.play().catch((e) => console.error("เสียงเล่นไม่สำเร็จ", e));
          beepPlayedRef.current = true;
        }
      }
    }, 200);

    if (!open) {
      // reset เมื่อปิด dialog
      setPrice(0);
      setWeight(0);
      setDeduct(0);
      setFocused("price");
      setInputValue(0);
    }

    return () => {
      socket.close();
      clearInterval(interval);
    };
  }, [selectedValue, open, mute]);

  // ---------- คำนวณสุทธิแบบทันที ----------
  const calculateNetWeight = (w, d, i) => {
    const weightNum = parseFloat(w || 0);
    const deductNum = parseFloat(d || 0);
    return weightNum - deductNum;
  };

  const netWeight = useMemo(() => {
    return calculateNetWeight(weight, deduct);
  }, [weight, deduct]);

  const isNetNegative = netWeight < 0;

  const display2 = (val) =>
    val === "" || val == null || Number.isNaN(val)
      ? "0.00"
      : Number(val).toFixed(2);

  const sanitizeNumber = (s) => {
    if (s == null) return "";
    let x = String(s).replace(/[^\d.]/g, "");
    const parts = x.split(".");
    if (parts.length > 2) x = parts[0] + "." + parts.slice(1).join("");
    if (/^0+\d/.test(x)) x = x.replace(/^0+/, "");
    return x;
  };

  // ---------- sync keypad ตามช่องที่โฟกัส ----------
  useEffect(() => {
    if (focused === null) return;
    setInputValue(focused === "weight" ? weight || 0 : deduct || 0);
  }, [focused, weight, deduct]);

  // ========== NEW: Keyboard handler — พิมพ์จากคีย์บอร์ดอัปเดตทันที (ไม่ต้องกด OK) ==========
  useEffect(() => {
    if (!open) return;

    const isTypingInTextInput = () => {
      const el = document.activeElement;
      if (!el) return false;
      const tag = el.tagName?.toLowerCase();
      const isInputLike =
        tag === "input" ||
        tag === "textarea" ||
        el.getAttribute?.("contenteditable") === "true";
      return isInputLike;
    };

    const commitLive = (nextStr) => {
      const v = sanitizeNumber(nextStr);
      // อัปเดต inputValue ให้ Keypad แสดงตาม
      setInputValue(v === "" ? 0 : v);

      // อัปเดตฟิลด์ที่โฟกัสทันที
      if (focused === "price") {
        setPrice(v);
      } else if (focused === "weight") {
        setWeight(v);
      } else if (focused === "deduct") {
        setDeduct(v);
      }
    };

    const onKeyDown = (e) => {
      // ถ้ามี dialog อื่นเปิดอยู่ ให้ผ่าน
      if (openMainProdDialog) return;

      // ถ้าพิมพ์อยู่ใน input จริง ๆ แล้วปล่อยให้ onChange ของ TextField จัดการเอง
      if (isTypingInTextInput()) return;

      const editing =
        focused === "price" || focused === "weight" || focused === "deduct";
      if (!editing) return;

      const key = e.key;

      // 0-9 .
      if ((key >= 0 && key <= "9") || key === ".") {
        e.preventDefault();
        const cur = String(inputValue ?? 0);
        if (key === "." && cur.includes(".")) return;
        const next = cur === 0 && key !== "." ? key : (cur || "") + String(key);
        commitLive(next);
        return;
      }

      // Backspace
      if (key === "Backspace") {
        e.preventDefault();
        const cur = String(inputValue ?? 0);
        const next = cur.length <= 1 ? 0 : cur.slice(0, -1);
        commitLive(next);
        return;
      }

      // Escape -> reset current
      if (key === "Escape") {
        e.preventDefault();
        commitLive(0);
        return;
      }

      // Enter: ไม่ต้องกด OK แล้ว แต่ถ้ากดก็ไม่ทำอะไรเพิ่ม (คงค่า)
      if (key === "Enter") {
        e.preventDefault();
        return;
      }
    };

    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [open, inputValue, focused, openMainProdDialog]);
  // ================== END Keyboard handler ==================

  const handleData = (event) => {
    const data = JSON.parse(event.data);
    if (data.weight != null) {
      const rawWeight = Number(data.weight);
      const roundedWeight = parseFloat(rawWeight.toFixed(3));
      if (
        lastWeightRef.current === null ||
        Math.abs(roundedWeight - lastWeightRef.current) > 0.05
      ) {
        lastWeightRef.current = roundedWeight;
        stableStartTimeRef.current = Date.now();
        beepPlayedRef.current = false;
      }
    }
  };

  const captureImage = useCallback(async () => {
    if (!webcamRef.current) return null;
    const video = webcamRef.current.video;
    if (!video || video.readyState < 2) {
      console.warn("No webcam video available");
      return null;
    }
    const canvas = document.createElement("canvas");
    canvas.width = 1920;
    canvas.height = 1080;
    const ctx = canvas.getContext("2d");
    ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
    const imageSrc = canvas.toDataURL("image/jpeg", 1);
    if (!imageSrc) return null;
    const blob = await (await fetch(imageSrc)).blob();
    const file = new File([blob], "webcam_photo.jpg", { type: "image/jpeg" });
    return file;
  }, []);

  async function uploadFile(file) {
    if (!file) throw new Error("No file to upload");
    const formData = new FormData();
    formData.append("file", file);
    const response = await fetch(SALENG_SHOP_API + "/files", {
      method: "POST",
      body: formData,
    });
    if (!response.ok) throw new Error("Upload failed: " + response.statusText);
    const result = await response.json();
    return result;
  }

  const handleSummit = async () => {
    let imageUrl = null;
    try {
      const capturedImage = await captureImage();
      if (capturedImage) {
        const uploadResult = await uploadFile(capturedImage);
        imageUrl = uploadResult?.file ?? imageUrl;
      }
    } catch (err) {
      console.log("ถ่าย/อัปโหลดรูปไม่สำเร็จ จะดำเนินการต่อโดยไม่แนบรูป:", err);
    }

    try {
      // ใช้ product ปัจจุบันจาก dialog หากมี
      const productId = mainProduct?.id || selectedValue?.id;
      const productTitle = mainProduct?.title || selectedValue?.title;
      const categoryId = mainProduct?.categoryId ?? selectedValue?.categoryId;

      const netFromSubmit = parseFloat(weight || 0) - parseFloat(deduct || 0);

      onSubmit({
        ...selectedValue,
        id: productId,
        title: productTitle,
        categoryId,
        finalPrice: price,
        weight,
        deduct,
        netWeight: netFromSubmit,
        imageUrl,
      });

      // reset ฟอร์มที่จำเป็น
      setPrice(0);
      setWeight(0);
      setDeduct(0);
    } catch (error) {
      console.error("เกิดข้อผิดพลาดในการส่งข้อมูล:", error);
    }
  };

  // ---------------------- render ----------------------
  return (
    <>
      <Dialog open={open} onClose={onClose} maxWidth="xl" fullWidth>
        <DialogTitle sx={{ fontWeight: 700 }}>
          รายการสินค้า
          <IconButton
            onClick={onClose}
            sx={{ position: "absolute", right: 8, top: 8 }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>

        <DialogContent dividers sx={{ p: 2 }}>
          <Grid container spacing={4} mb={5}>
            {/* ซ้าย: กล้อง */}
            <Grid item xs={12} md={12} lg={3} sx={{ display: "flex" }}>
              <Box
                sx={{
                  borderRadius: 2,
                  overflow: "hidden",
                  border: "1px solid",
                  borderColor: theme.palette.divider,
                  flex: 1,
                  display: "flex",
                  backgroundColor: "black",
                  justifyContent: "center",
                  alignItems: "center",
                }}
              >
                <Webcam
                  ref={webcamRef}
                  audio={false}
                  screenshotFormat="image/jpeg"
                  screenshotQuality={1}
                  style={{
                    height: "100%",
                    width: "100%",
                    objectFit: "contain",
                  }}
                  videoConstraints={{
                    width: 1920,
                    height: 1080,
                    deviceId: "f31ac40d713aa844625c18515fd8d5bd04ae128a7d60e1ea3827435f641f5db0",
                    // frameRate: { ideal: 60 },
                  }}
                />
              </Box>
            </Grid>

            {/* กลาง: ฟอร์ม */}
            <Grid item xs={12} md={12} lg={6}>
              <Stack spacing={2} sx={{ pr: { md: 2 } }}>
                {/* สินค้า + ราคา */}
                <Box
                  display="flex"
                  alignItems="center"
                  justifyContent="space-between"
                  gap={2}
                >
                  <Box>
                    <Typography variant="body2" color="text.secondary">
                      สินค้า
                    </Typography>
                    <Box display="flex" alignItems="center" gap={1}>
                      <Typography variant="h6" sx={{ fontWeight: 700 }}>
                        {mainProduct?.title || selectedValue?.title}
                      </Typography>
                      <IconButton
                        size="small"
                        onClick={() => setOpenMainProdDialog(true)}
                        title="แก้ไขสินค้า"
                        sx={{ bgcolor: "action.hover" }}
                      >
                        <EditIcon fontSize="small" />
                      </IconButton>
                    </Box>
                  </Box>

                  <Box display="flex" alignItems="center" gap={1}>
                    <Box>
                      <Typography
                        variant="body2"
                        sx={{ mb: 0.5 }}
                        color="text.secondary"
                      >
                        ราคาสินค้า
                      </Typography>
                      <TextField
                        fullWidth
                        value={formatNumber(price ?? 0)}
                        onFocus={() => {
                          setFocused("price");
                          setInputValue(price || 0);
                        }}
                        InputProps={{
                          readOnly: true,
                          sx: {
                            fontWeight: 700,
                            fontSize: 24,
                            bgcolor:
                              focused === "price"
                                ? "action.hover"
                                : "background.paper",
                          },
                          endAdornment: (
                            <InputAdornment position="end">
                              <Typography color="text.secondary">
                                บาท/กก.
                              </Typography>
                            </InputAdornment>
                          ),
                        }}
                      />
                    </Box>
                  </Box>
                </Box>

                {/* น้ำหนัก รวม / หัก */}
                <Grid container>
                  <Grid item xs={12} md={12}>
                    <Typography
                      variant="body2"
                      sx={{ mb: 0.5 }}
                      color="text.secondary"
                    >
                      รวม
                    </Typography>
                    <TextField
                      fullWidth
                      value={formatNumber(weight)}
                      onFocus={() => {
                        setFocused("weight");
                        setInputValue(weight || 0);
                      }}
                      InputProps={{
                        readOnly: true,
                        sx: {
                          fontWeight: 700,
                          fontSize: 24,
                          bgcolor:
                            focused === "weight"
                              ? "action.hover"
                              : "background.paper",
                        },
                        endAdornment: (
                          <InputAdornment position="end">
                            <Typography color="text.secondary">กก.</Typography>
                          </InputAdornment>
                        ),
                      }}
                    />
                  </Grid>

                  <Grid item xs={12} md={12} mt={1}>
                    <Typography
                      variant="body2"
                      sx={{ mb: 0.5 }}
                      color="text.secondary"
                    >
                      หัก
                    </Typography>
                    <TextField
                      fullWidth
                      value={formatNumber(deduct)}
                      onFocus={() => {
                        setFocused("deduct");
                        setInputValue(deduct || 0);
                      }}
                      InputProps={{
                        readOnly: true,
                        sx: {
                          fontWeight: 700,
                          fontSize: 24,
                          bgcolor:
                            focused === "deduct"
                              ? "action.hover"
                              : "background.paper",
                        },
                        endAdornment: (
                          <InputAdornment position="end">
                            <Typography color="text.secondary">กก.</Typography>
                          </InputAdornment>
                        ),
                      }}
                    />
                  </Grid>
                </Grid>

                {/* น้ำหนัก สุทธิ / รวมราคา */}
                <Grid container>
                  <Grid item xs={12}>
                    <Divider sx={{ mb: 2 }} />
                  </Grid>

                  <Grid item xs={12} md={6}>
                    <Typography
                      variant="body2"
                      sx={{ mb: 0.5 }}
                      color="text.secondary"
                    >
                      สุทธิ
                    </Typography>
                    <Typography
                      sx={{
                        fontWeight: 700,
                        fontSize: 24,
                        color: isNetNegative ? "error.main" : "text.primary",
                      }}
                    >
                      {display2(netWeight)}{" "}
                      <Typography component="span" color="text.secondary">
                        กก.
                      </Typography>
                    </Typography>
                    {isNetNegative && (
                      <Typography variant="caption" color="error.main">
                        สุทธิห้ามติดลบ
                      </Typography>
                    )}
                  </Grid>

                  <Grid item xs={12} md={6}>
                    <Typography
                      variant="body2"
                      sx={{ mb: 0.5 }}
                      color="text.secondary"
                    >
                      รวมราคา
                    </Typography>
                    <Typography sx={{ fontWeight: 700, fontSize: 24 }}>
                      {formatNumber(netWeight * price)}{" "}
                      <Typography component="span" color="text.secondary">
                        บาท
                      </Typography>
                    </Typography>
                  </Grid>
                </Grid>
              </Stack>
            </Grid>

            {/* ขวา: Keypad */}
            <Grid
              item
              xs={12}
              md={12}
              lg={3}
              sx={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
              }}
            >
              <Paper elevation={0} sx={{ p: 1 }}>
                <Keypad
                  value={inputValue}
                  onChange={(val) => setInputValue(val)}
                  onClear={() => {
                    setInputValue(0);
                    if (focused === "weight") setWeight(0);
                    if (focused === "deduct") setDeduct(0);
                    // netWeight คำนวณผ่าน useMemo อยู่แล้ว
                  }}
                  onSubmit={(result) => {
                    // กด OK บน Keypad เท่านั้นถึงคอมมิต
                    const val = result;
                    if (focused === "price") {
                      setPrice(val);
                      setFocused("weight");
                      setInputValue(weight || 0);
                    } else if (focused === "weight") {
                      setWeight(val);
                      setFocused("deduct");
                      setInputValue(deduct || 0);
                    } else if (focused === "deduct") {
                      setDeduct(val);
                      setFocused("weight");
                      setInputValue(weight || 0);
                    }
                  }}
                />
              </Paper>
            </Grid>
          </Grid>
        </DialogContent>

        <DialogActions sx={{ p: 0 }}>
          <Button
            fullWidth
            variant="contained"
            onClick={handleSummit}
            sx={{
              borderRadius: 0,
              py: 1.8,
              fontWeight: 700,
              bgcolor: "#1E2630",
              "&:hover": { bgcolor: "#111820" },
            }}
          >
            ยืนยัน
          </Button>
        </DialogActions>
      </Dialog>

      {/* Dialog แก้ไขสินค้า (ตัวหลัก) */}
      <ProductSelectDialog
        open={openMainProdDialog}
        onClose={() => setOpenMainProdDialog(false)}
        onConfirm={(prod) => {
          setMainProduct({
            id: prod.id,
            title: prod.title,
            categoryId: prod.category?.id,
          });
          setOpenMainProdDialog(false);
        }}
        products={products}
        categories={categories}
        initialSelectedId={mainProduct?.id || selectedValue?.id}
      />
    </>
  );
};

export default SimpleDialog;
